import { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LinkSimple, CheckCircle, Circle, FilmSlate, Queue } from "@phosphor-icons/react";
import UploadVideo from "@/components/watch/UploadVideo";
import Playlist from "@/components/watch/Playlist";
import CoverBanner from "@/components/watch/CoverBanner";
import VoiceChat from "@/components/watch/VoiceChat";

const EMOJIS = ["😂", "😍", "😱", "🔥", "👏", "😭", "🤯", "❤️"];
const SUPPRESS_MS = 1200;
const BASE = process.env.REACT_APP_BACKEND_URL;

export default function WatchRoom({ user }) {
  const [room, setRoom] = useState(null);
  const [members, setMembers] = useState([]);
  const [url, setUrl] = useState("");
  const [floats, setFloats] = useState([]);
  const [ready, setReady] = useState({});
  const videoRef = useRef(null);
  const ytRef = useRef(null);
  const wsRef = useRef(null);
  const roomRef = useRef(null);
  const voiceRef = useRef(null);
  const suppress = useRef(0);
  roomRef.current = room;

  const load = useCallback(() => {
    api.get("/watch/room").then((r) => { setRoom(r.data.room); setMembers(r.data.members); setReady(r.data.room.ready || {}); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // WebSocket
  useEffect(() => {
    let alive = true, poll;
    const connect = () => {
      const ws = new WebSocket(`${BASE.replace(/^http/, "ws")}/api/ws/chat`);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        const d = JSON.parse(e.data);
        if (d.type !== "watch") return;
        if (d.event === "source") {
          setRoom(d.room); setReady(d.room.ready || {});
          toast.info(`${d.by || "Кто-то"} включил(а): ${d.room.title}`);
        }
        if (d.event === "playlist") setRoom(d.room);
        if (d.event === "cover") { setRoom(d.room); toast.info(`${d.by || "Кто-то"} обновил(а) обложку`); }
        if (d.event === "voice" || d.event === "voice-signal") { voiceRef.current?.onEvent(d); return; }
        if (d.event === "control") applyRemote(d.action, d.position);
        if (d.event === "ready") { setReady(d.ready || {}); if (d.all_ready) toast.success("Все готовы — поехали!"); }
        if (d.event === "react") pushFloat(d.emoji, d.by);
      };
      ws.onclose = () => { if (alive) poll = setTimeout(connect, 4000); };
    };
    connect();
    return () => { alive = false; clearTimeout(poll); wsRef.current?.close(); };
  }, []);

  const pushFloat = (emoji, by) => {
    const id = Math.random();
    setFloats((f) => [...f, { id, emoji, by, left: 10 + Math.random() * 75 }]);
    setTimeout(() => setFloats((f) => f.filter((x) => x.id !== id)), 3000);
  };

  const applyRemote = (action, position) => {
    suppress.current = Date.now() + SUPPRESS_MS;
    const v = videoRef.current;
    if (v) {
      if (position != null && Math.abs(v.currentTime - position) > 1.2) v.currentTime = position;
      if (action === "play") v.play().catch(() => {});
      else if (action === "pause") v.pause();
    }
    const yt = ytRef.current;
    if (yt) {
      if (position != null && Math.abs(yt.getCurrentTime() - position) > 1.2) yt.seekTo(position, true);
      if (action === "play") yt.playVideo();
      else if (action === "pause") yt.pauseVideo();
    }
  };

  const sendControl = (action, position) => {
    if (Date.now() < suppress.current) return;
    api.post("/watch/control", { action, position: position ?? 0 }).catch(() => {});
  };

  const onEnded = () => {
    api.post("/watch/ended", { item_id: roomRef.current?.current_item }).catch(() => {});
  };

  const setSource = async () => {
    if (!url.trim()) return;
    try { await api.post("/watch/source", { url }); setUrl(""); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const addToPlaylist = async () => {
    if (!url.trim()) return;
    try {
      const { data } = await api.post("/watch/playlist/add", { url });
      setRoom(data.room); setUrl(""); toast.success("Добавлено в плейлист");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleReady = async () => {
    const mine = !ready[user.id];
    try { const { data } = await api.post("/watch/ready", { ready: mine }); setReady(data.ready); } catch (e) { toast.error(formatApiError(e)); }
  };

  const react = (emoji) => { pushFloat(emoji, "Вы"); api.post("/watch/react", { emoji }).catch(() => {}); };

  // YouTube IFrame API
  useEffect(() => {
    if (room?.kind !== "youtube") { ytRef.current = null; return; }
    const init = () => {
      ytRef.current = new window.YT.Player("yt-player", {
        videoId: room.ref, playerVars: { autoplay: room.playing ? 1 : 0, rel: 0 },
        events: {
          onReady: (e) => { if (room.position) e.target.seekTo(room.position, true); if (room.playing) e.target.playVideo(); },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.ENDED) { onEnded(); return; }
            if (Date.now() < suppress.current) return;
            const t = e.target.getCurrentTime();
            if (e.data === window.YT.PlayerState.PLAYING) sendControl("play", t);
            else if (e.data === window.YT.PlayerState.PAUSED) sendControl("pause", t);
          },
        },
      });
    };
    if (!window.YT || !window.YT.Player) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
      window.onYouTubeIframeAPIReady = init;
    } else init();
    return () => { try { ytRef.current?.destroy(); } catch {} ytRef.current = null; };
  }, [room?.kind, room?.ref]);

  const iAmReady = ready[user?.id];
  const readyCount = members.filter((m) => ready[m.id]).length;
  const videoSrc = room?.kind === "stream" ? `${BASE}/api/watch/stream/${room.ref}` : room?.ref;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4" data-testid="watch-room">
      <div className="space-y-3">
        <CoverBanner room={room} onRoom={setRoom} />

        <div className="glass-card p-3 flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <LinkSimple size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setSource()}
              placeholder="Ссылка YouTube, Vimeo или на файл (mp4/webm)" className="pl-9" data-testid="watch-url-input" />
          </div>
          <Button onClick={setSource} data-testid="watch-set-source"><FilmSlate size={16} className="mr-1.5" />Включить</Button>
          <Button variant="outline" onClick={addToPlaylist} data-testid="watch-add-playlist"><Queue size={16} className="mr-1.5" />В очередь</Button>
          <UploadVideo onDone={(r) => { setRoom(r); setReady(r.ready || {}); }} />
        </div>

        <VoiceChat ref={voiceRef} user={user} />

        <div className="glass-card overflow-hidden relative aspect-video bg-black" data-testid="watch-player">
          {!room?.kind && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground gap-2">
              <FilmSlate size={40} weight="duotone" /><span className="text-sm">Вставьте ссылку или загрузите видеофайл, чтобы начать совместный просмотр</span>
            </div>
          )}
          {room?.kind === "youtube" && <div id="yt-player" className="w-full h-full" />}
          {room?.kind === "vimeo" && (
            <iframe title="vimeo" src={`https://player.vimeo.com/video/${room.ref}`} className="w-full h-full" allow="autoplay; fullscreen" frameBorder="0" />
          )}
          {(room?.kind === "direct" || room?.kind === "stream") && (
            <video key={room.ref} ref={videoRef} src={videoSrc} controls autoPlay={room.playing} className="w-full h-full" data-testid="watch-video"
              onPlay={() => sendControl("play", videoRef.current?.currentTime)}
              onPause={() => sendControl("pause", videoRef.current?.currentTime)}
              onSeeked={() => sendControl("seek", videoRef.current?.currentTime)}
              onEnded={onEnded} />
          )}
          {floats.map((f) => (
            <div key={f.id} className="absolute bottom-16 text-3xl pointer-events-none watch-float" style={{ left: `${f.left}%` }}>
              {f.emoji}<span className="block text-[10px] text-white/70 text-center">{f.by}</span>
            </div>
          ))}
        </div>

        {room?.kind === "vimeo" && (
          <p className="text-[11px] text-muted-foreground">Vimeo синхронизирует старт; для точной паузы используйте YouTube или файл.</p>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={toggleReady} variant={iAmReady ? "default" : "outline"} data-testid="watch-ready-btn" className="active:scale-95 transition-transform">
            {iAmReady ? <CheckCircle size={16} weight="fill" className="mr-1.5" /> : <Circle size={16} className="mr-1.5" />}
            {iAmReady ? "Я готов(а)" : "Отметить готовность"}
          </Button>
          <span className="text-xs text-muted-foreground num">Готовы: {readyCount}/{members.length}</span>
          <div className="flex gap-1 ml-auto">
            {EMOJIS.map((e) => (
              <button key={e} onClick={() => react(e)} data-testid={`watch-react-${e}`}
                className="text-xl hover:scale-125 transition-transform active:scale-90">{e}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <Playlist room={room} onRoom={setRoom} />
        <div className="glass-card p-4 h-fit">
          <h3 className="font-heading font-semibold text-sm mb-2">Кто в комнате</h3>
          <div className="space-y-1.5">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-2 text-sm" data-testid={`watch-member-${m.id}`}>
                {ready[m.id] ? <CheckCircle size={15} weight="fill" className="text-[#00e676]" /> : <Circle size={15} className="text-muted-foreground" />}
                <span>{m.name}{m.id === user?.id ? " (вы)" : ""}</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Пауза, старт и перемотка синхронизируются у всех. Когда серия закончится — плейлист сам включит следующую.</p>
        </div>
      </div>
    </div>
  );
}
